package com.shadowings.gero

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.data.repository.UniliteralRepository
import com.shadowings.gero.domain.model.ThemeMode
import com.shadowings.gero.domain.model.currentChangelogEntry
import com.shadowings.gero.presentation.component.DailyBonusDialog
import com.shadowings.gero.presentation.model.RewardedAdStatus
import com.shadowings.gero.presentation.navigation.AppNavGraph
import com.shadowings.gero.presentation.screen.ChangelogDialog
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.LocalIsDarkMode
import com.shadowings.gero.presentation.ui.hieroglyphTint
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import com.shadowings.gero.presentation.viewmodel.AppViewModel
import com.shadowings.gero.util.AppUpdateChecker
import com.shadowings.gero.util.HapticVibrationManager
import com.shadowings.gero.util.ToggleableHapticFeedback
import com.shadowings.gero.util.setAppLanguage
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.loading_text
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun AppRoot(
    purchaseFullVersion: () -> Unit = {},
    restoreFullVersionPurchases: () -> Unit = {},
    isFullVersionUnlocked: Boolean = false,
    isPurchaseReady: Boolean = false,
    watchAdForHearts: () -> Unit = {},
    adStatus: RewardedAdStatus = RewardedAdStatus.LOADING,
    initialThemeMode: ThemeMode,
    onResolvedDarkThemeChanged: (Boolean) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val viewModel = koinViewModel<AppViewModel>()
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val navController = rememberNavController()

    // Apply the language whenever it changes. On web, Compose's Locale.current reads
    // navigator.language/.languages live and has no Configuration-change hook like Android/iOS,
    // so after setAppLanguage() overrides it, languageEpoch is bumped to force the content below
    // (wrapped in key(languageEpoch)) to fully recompose against the new locale. Android/iOS
    // already recompose correctly on their own without this — bumping there too used to tear
    // down and rebuild the whole subtree (nav graph + any dialog on top) the moment the real
    // persisted language replaced the empty default at cold start, causing a visible flicker.
    var languageEpoch by remember { mutableStateOf(0) }
    LaunchedEffect(uiState.language) {
        setAppLanguage(uiState.language)
        if (getPlatform().isWeb) {
            languageEpoch++
        }
    }

    val themeMode = if (uiState.tutorialDone == null) initialThemeMode else uiState.themeMode
    val effectiveIsFullVersionUnlocked = if (getPlatform().isWeb) {
        uiState.userData.isFullVersionUnlocked
    } else {
        isFullVersionUnlocked
    }
    // On web the intro walkthrough only exists to ask for notification permission, which is a
    // no-op there anyway (see NotificationPermission.js.kt) — skip straight to the landing screen.
    val effectiveTutorialDone = uiState.tutorialDone == true || getPlatform().isWeb

    LaunchedEffect(isFullVersionUnlocked) {
        if (isFullVersionUnlocked && !uiState.userData.isFullVersionUnlocked) {
            viewModel.setFullVersionUnlocked(true)
        }
    }

    LaunchedEffect(uiState.tutorialDone) {
        if (getPlatform().isWeb && uiState.tutorialDone == false) {
            viewModel.completeTutorialForWeb()
        }
    }

    // TEMP diagnostic: now that AuthRepository resolves eagerly at boot (see initKoin()), test
    // whether holding first paint a bit longer on web lets auth settle before the landing screen
    // ever renders, sidestepping the repaint issue instead of fixing it. Tune/remove once we know.
    var webStartupDelayElapsed by remember { mutableStateOf(!getPlatform().isWeb) }
    LaunchedEffect(Unit) {
        if (getPlatform().isWeb) {
            delay(2000.milliseconds)
            webStartupDelayElapsed = true
        }
    }

    val platformHaptic = LocalHapticFeedback.current
    val toggleableHaptic = remember(platformHaptic, uiState.hapticEnabled) {
        ToggleableHapticFeedback(platformHaptic) { uiState.hapticEnabled }
    }

    SideEffect {
        HapticVibrationManager.isEnabled = uiState.hapticEnabled
    }

    val deviceInfo = rememberDeviceInfo()

    CompositionLocalProvider(
        LocalHapticFeedback provides toggleableHaptic
    ) {
        AppTheme(themeMode = themeMode) {
            val isDarkTheme = LocalIsDarkMode.current
            SideEffect {
                onResolvedDarkThemeChanged(isDarkTheme)
            }

            // Check for updates on startup
            AppUpdateChecker()

            // On web, forces the whole subtree below (and every stringResource() call in it)
            // to fully recompose against the newly-applied locale — see languageEpoch above.
            key(languageEpoch) {
                Box(
                    modifier = modifier
                        .fillMaxSize()
                        .background(
                            androidx.compose.material3.MaterialTheme.colorScheme.background
                        ),
                    contentAlignment = Alignment.Center
                ) {

                    GardinerPatternBackground(
                        modifier = Modifier.matchParentSize()
                    )

                    val sideBorderColor = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.5f)
                    Box(
                        modifier = modifier
                            .fillMaxSize()
                            .then(
                                if (deviceInfo.deviceClass == DeviceClass.WEB_FULL) {
                                    Modifier.padding(horizontal = 256.dp)
                                } else {
                                    Modifier
                                }
                            )
                            .drawBehind {
                                if (deviceInfo.deviceClass == DeviceClass.WEB_FULL) {
                                    val strokeWidthPx = 1.dp.toPx()
                                    val halfStroke = strokeWidthPx / 2f
                                    drawLine(
                                        color = sideBorderColor,
                                        start = Offset(halfStroke, 0f),
                                        end = Offset(halfStroke, size.height),
                                        strokeWidth = strokeWidthPx
                                    )
                                    drawLine(
                                        color = sideBorderColor,
                                        start = Offset(size.width - halfStroke, 0f),
                                        end = Offset(size.width - halfStroke, size.height),
                                        strokeWidth = strokeWidthPx
                                    )
                                }
                            },
                        contentAlignment = Alignment.Center
                    ) {

                        // Aspetta che userData sia caricato dal DataStore e che il database sia pronto
                        if (uiState.tutorialDone != null && uiState.isDatabaseReady && webStartupDelayElapsed) {
                            AppNavGraph(
                                navController = navController,
                                tutorialDone = effectiveTutorialDone,
                                purchaseFullVersion = purchaseFullVersion,
                                restoreFullVersionPurchases = restoreFullVersionPurchases,
                                isFullVersionUnlocked = effectiveIsFullVersionUnlocked,
                                isPurchaseReady = isPurchaseReady,
                                watchAdForHearts = watchAdForHearts,
                                adStatus = adStatus,
                            )

                            if (uiState.showDailyBonus) {
                                DailyBonusDialog(
                                    isFullVersionUnlocked = effectiveIsFullVersionUnlocked,
                                    userData = uiState.userData,
                                    onClaim = {
                                        viewModel.claimDailyBonus(
                                            effectiveIsFullVersionUnlocked
                                        )
                                    }
                                )
                            } else if (uiState.showChangelog) {
                                ChangelogDialog(
                                    entry = currentChangelogEntry,
                                    onDismiss = { viewModel.dismissChangelog() }
                                )
                            }
                        } else {
                            androidx.compose.material3.Scaffold(
                                modifier = Modifier.fillMaxSize(),
                                containerColor = androidx.compose.material3.MaterialTheme.colorScheme.background
                            ) { padding ->
                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .padding(padding),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.spacedBy(24.dp)
                                    ) {
                                        androidx.compose.material3.CircularProgressIndicator(
                                            modifier = Modifier.size(72.dp),
                                            color = androidx.compose.material3.MaterialTheme.colorScheme.primary,
                                            trackColor = androidx.compose.material3.MaterialTheme.colorScheme.surfaceVariant,
                                            strokeWidth = 6.dp,
                                            strokeCap = androidx.compose.ui.graphics.StrokeCap.Round
                                        )
                                        androidx.compose.material3.Text(
                                            text = stringResource(Res.string.loading_text),
                                            style = androidx.compose.material3.MaterialTheme.typography.titleMedium,
                                            color = androidx.compose.material3.MaterialTheme.colorScheme.onBackground
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// A small curated set of recognizable Gardiner sign codes, tiled from the app's existing
// per-sign drawables as a low-opacity wallpaper, WhatsApp-chat-background style.
private val gardinerPatternCodes = listOf(
    "A1", "G17", "N35", "D21", "M17", "Q3", "O1", "G43",
    "X1", "N5", "F31", "P1", "D4", "G7", "N1"
)

@Composable
private fun GardinerPatternBackground(
    modifier: Modifier = Modifier
) {
    val symbolColor = hieroglyphTint.copy(alpha = if (LocalIsDarkMode.current) 0.06f else 0.05f)
    val colorFilter = remember(symbolColor) { ColorFilter.tint(symbolColor) }
    val painters = gardinerPatternCodes.mapNotNull { code ->
        UniliteralRepository.resFromGardinerCode(code)?.let { painterResource(it) }
    }
    if (painters.isEmpty()) return

    Canvas(modifier = modifier) {
        val tileSize = 56.dp.toPx()
        val symbolSize = Size(32.dp.toPx(), 32.dp.toPx())
        val cols = (size.width / tileSize).toInt() + 2
        val rows = (size.height / tileSize).toInt() + 2

        for (row in -1..rows) {
            val rowOffset = if (row % 2 == 0) 0f else tileSize / 2f
            for (col in -1..cols) {
                val painterIndex = (row * 5 + col * 3).mod(painters.size)
                val painter = painters[painterIndex]
                translate(left = col * tileSize + rowOffset, top = row * tileSize) {
                    with(painter) {
                        draw(size = symbolSize, colorFilter = colorFilter)
                    }
                }
            }
        }
    }
}


@Composable
fun App(
    purchaseFullVersion: () -> Unit = {},
    restoreFullVersionPurchases: () -> Unit = {},
    isFullVersionUnlocked: Boolean = false,
    isPurchaseReady: Boolean = false,
    watchAdForHearts: () -> Unit = {},
    adStatus: RewardedAdStatus = RewardedAdStatus.LOADING,
    initialThemeMode: ThemeMode = ThemeMode.System,
    onResolvedDarkThemeChanged: (Boolean) -> Unit = {},
    modifier: Modifier = Modifier
) {
    AppRoot(
        purchaseFullVersion = purchaseFullVersion,
        restoreFullVersionPurchases = restoreFullVersionPurchases,
        isFullVersionUnlocked = isFullVersionUnlocked,
        isPurchaseReady = isPurchaseReady,
        watchAdForHearts = watchAdForHearts,
        adStatus = adStatus,
        initialThemeMode = initialThemeMode,
        onResolvedDarkThemeChanged = onResolvedDarkThemeChanged,
        modifier = modifier,
    )
}
